
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Edit, Trash, Eye, RotateCcw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface Product {
  id: string;
  name: string;
  price: number;
  compare_at_price?: number;
  stock_quantity?: number;
  status: string;
  featured: boolean;
  image_url?: string;
  categories?: { name: string };
  created_at: string;
}

interface ProductsTableProps {
  products: Product[];
  categories: any[];
  isLoading: boolean;
  onEdit: (product: Product) => void;
  onRefresh: () => void;
}

const ProductsTable = ({ products, isLoading, onEdit, onRefresh }: ProductsTableProps) => {
  const { toast } = useToast();

  const deleteMutation = useMutation({
    mutationFn: async (productId: string) => {
      // First try to hard delete the product
      const { error: deleteError } = await supabase
        .from('products')
        .delete()
        .eq('id', productId);
      
      // If hard delete fails due to foreign key constraints, soft delete instead
      if (deleteError && deleteError.code === '23503') {
        const { error: softDeleteError } = await supabase
          .from('products')
          .update({ status: 'inactive' })
          .eq('id', productId);
        
        if (softDeleteError) throw softDeleteError;
        return { softDeleted: true };
      }
      
      if (deleteError) throw deleteError;
      return { softDeleted: false };
    },
    onSuccess: (result) => {
      toast({
        title: "Success",
        description: result?.softDeleted 
          ? "Product archived (has order history)" 
          : "Product deleted successfully",
      });
      onRefresh();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete product",
        variant: "destructive",
      });
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: async (productId: string) => {
      const { error } = await supabase
        .from('products')
        .update({ status: 'active' })
        .eq('id', productId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Product reactivated successfully",
      });
      onRefresh();
    },
    onError: (error) => {
      toast({
        title: "Error", 
        description: "Failed to reactivate product",
        variant: "destructive",
      });
    },
  });

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
      active: "default",
      draft: "secondary",
      archived: "outline",
      inactive: "destructive",
    };
    
    const variant = statusMap[status] || "outline";
    
    return (
      <Badge variant={variant}>
        {status}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <Card className="admin-card border-admin-border">
        <CardHeader>
          <CardTitle className="text-admin-foreground">Products</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center space-x-4">
                <Skeleton className="h-12 w-12 rounded" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-4 w-[250px]" />
                  <Skeleton className="h-4 w-[200px]" />
                </div>
                <div className="space-x-2">
                  <Skeleton className="h-8 w-16" />
                  <Skeleton className="h-8 w-16" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="admin-card border-admin-border">
      <CardHeader>
        <CardTitle className="text-admin-foreground">
          Products ({products.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Stock</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Featured</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((product) => (
                <TableRow key={product.id}>
                  <TableCell>
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-admin-muted rounded-lg flex items-center justify-center">
                        {product.image_url ? (
                          <img
                            src={product.image_url}
                            alt={product.name}
                            className="w-full h-full object-cover rounded-lg"
                          />
                        ) : (
                          <Eye className="h-4 w-4 text-admin-muted-foreground" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-admin-foreground">{product.name}</p>
                        <p className="text-sm text-admin-muted-foreground">
                          {new Date(product.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-admin-foreground">
                      {product.categories?.name || 'Uncategorized'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <p className="font-medium text-admin-foreground">
                        ${product.price.toFixed(2)}
                      </p>
                      {product.compare_at_price && (
                        <p className="text-sm text-admin-muted-foreground line-through">
                          ${product.compare_at_price.toFixed(2)}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={`font-medium ${
                      (product.stock_quantity || 0) > 0 
                        ? 'text-green-400' 
                        : 'text-red-400'
                    }`}>
                      {product.stock_quantity || 0}
                    </span>
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(product.status)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={product.featured ? "default" : "outline"}>
                      {product.featured ? "Yes" : "No"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(product)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      {product.status === 'inactive' ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => reactivateMutation.mutate(product.id)}
                          title="Reactivate product"
                        >
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      ) : (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <Trash className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Product</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete "{product.name}"? 
                                If this product has order history, it will be archived instead of permanently deleted.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteMutation.mutate(product.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProductsTable;
